import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
import emailjs from '@emailjs/browser';
export function ContactSection() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({
    type: null,
    message: ''
  });
  const formRef = useRef<HTMLFormElement>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({
      type: null,
      message: ''
    });
    // EmailJS configuration with actual values
    const serviceId = 'service_tssw5oa';
    const templateId = 'template_n0i17pc';
    const publicKey = 'x9SabP7t6qZW31W2T';
    emailjs.sendForm(serviceId, templateId, formRef.current!, publicKey).then(result => {
      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your message! We will get back to you soon.'
      });
      // Reset form
      setFormState({
        name: '',
        email: '',
        phone: '',
        eventType: '',
        message: ''
      });
    }).catch(error => {
      console.error('EmailJS error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send your message. Please try again or contact us directly.'
      });
    }).finally(() => {
      setIsSubmitting(false);
    });
  };
  return <section id="contact" className="py-16 bg-white" data-id="element-39">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-40">
        <AnimatedSection data-id="element-41">
          <div className="text-center mb-12" data-id="element-42">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-43">
              Let's Create Your Event
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto" data-id="element-44">
              Ready to start planning? Get in touch with our team.
            </p>
          </div>
        </AnimatedSection>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-id="element-45">
          <AnimatedSection delay={0.2} className="lg:col-span-2" data-id="element-46">
            <div className="bg-indigo-50 p-8 rounded-lg h-full" data-id="element-47">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-48">
                Contact Information
              </h3>
              <div className="space-y-6" data-id="element-49">
                <div className="flex items-start" data-id="element-50">
                  <Phone className="h-6 w-6 text-indigo-600 mt-1 mr-4" data-id="element-51" />
                  <div data-id="element-52">
                    <p className="text-sm font-medium text-gray-500" data-id="element-53">Phone</p>
                    <p className="text-base text-gray-900" data-id="element-54">630-615-9901</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-55">
                  <Mail className="h-6 w-6 text-indigo-600 mt-1 mr-4" data-id="element-56" />
                  <div data-id="element-57">
                    <p className="text-sm font-medium text-gray-500" data-id="element-58">Email</p>
                    <p className="text-base text-gray-900" data-id="element-59">dan@turnout.info</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-60">
                  <MapPin className="h-6 w-6 text-indigo-600 mt-1 mr-4" data-id="element-61" />
                  <div data-id="element-62">
                    <p className="text-sm font-medium text-gray-500" data-id="element-63">Address</p>
                    <p className="text-base text-gray-900" data-id="element-64">
                      1755 University Avenue
                      <br data-id="element-65" />
                      Palo Alto, CA 94301
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8" data-id="element-66">
                <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-67">
                  Office Hours
                </h4>
                <p className="text-gray-700" data-id="element-68">
                  Monday - Friday: 9:00 AM - 6:00 PM
                </p>
                <p className="text-gray-700" data-id="element-69">Saturday: 10:00 AM - 2:00 PM</p>
                <p className="text-gray-700" data-id="element-70">Sunday: Closed</p>
              </div>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.4} className="lg:col-span-3" data-id="element-71">
            <div className="bg-white rounded-lg shadow-md p-8 h-full" data-id="element-72">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-73">
                Send Us a Message
              </h3>
              {submitStatus.type === 'success' && <div className="mb-6 p-4 bg-green-50 text-green-800 rounded-md" data-id="element-74">
                  {submitStatus.message}
                </div>}
              {submitStatus.type === 'error' && <div className="mb-6 p-4 bg-red-50 text-red-800 rounded-md" data-id="element-75">
                  {submitStatus.message}
                </div>}
              <form ref={formRef} onSubmit={handleSubmit} data-id="element-76">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-77">
                  <div data-id="element-78">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-79">
                      Full Name *
                    </label>
                    <input type="text" id="name" name="name" value={formState.name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" data-id="element-80" />
                  </div>
                  <div data-id="element-81">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-82">
                      Email Address *
                    </label>
                    <input type="email" id="email" name="email" value={formState.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" data-id="element-83" />
                  </div>
                  <div data-id="element-84">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-85">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" value={formState.phone} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" data-id="element-86" />
                  </div>
                  <div data-id="element-87">
                    <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-88">
                      Event Type *
                    </label>
                    <select id="eventType" name="eventType" value={formState.eventType} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" data-id="element-89">
                      <option value="" data-id="element-90">Select an event type</option>
                      <option value="corporate" data-id="element-91">Corporate Event</option>
                      <option value="wedding" data-id="element-92">Wedding</option>
                      <option value="social" data-id="element-93">Social Gathering</option>
                      <option value="conference" data-id="element-94">Conference</option>
                      <option value="other" data-id="element-95">Other</option>
                    </select>
                  </div>
                </div>
                <div className="mb-6" data-id="element-96">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-97">
                    Tell us about your event *
                  </label>
                  <textarea id="message" name="message" value={formState.message} onChange={handleChange} required rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" data-id="element-98"></textarea>
                </div>
                <div data-id="element-99">
                  <button type="submit" disabled={isSubmitting} className={`w-full px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-300 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`} data-id="element-100">
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>;
}